import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HomologationSourceType } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { academicApi } from '@/services/academic.api';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';
import { PersonPicker } from '@/modules/people/person-picker';

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/** "Homologaciones" — credit recognized for a subject the student never
 *  actually took here, internal (another school of the church) or external
 *  (another institution, with a certificate). Staff with
 *  `academic.homologation.read` see and filter every request; everyone
 *  else sees only their own. */
export function HomologationsListPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canReview = can('academic.homologation.manage');
  const canSeeAll = can('academic.homologation.read');

  const schools = useQuery({
    queryKey: ['academic', 'schools', false],
    queryFn: ({ signal }) => academicApi.listSchools(false, signal),
  });

  const homologations = useQuery({
    queryKey: ['academic', 'homologations', canSeeAll],
    queryFn: ({ signal }) =>
      canSeeAll ? academicApi.listHomologations({}, signal) : academicApi.myHomologations(signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['academic', 'homologations'] });

  const review = useMutation({
    mutationFn: (input: { id: string; status: 'APPROVED' | 'REJECTED' }) =>
      academicApi.reviewHomologation(input.id, { status: input.status }),
    onSuccess: invalidate,
  });

  const schoolList = schools.data ?? [];
  const list = homologations.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('academic.homologation.title')}</h1>
        </div>
      </header>

      {schoolList.length > 0 && (
        <RequestHomologationForm schools={schoolList} onRequested={invalidate} />
      )}

      {homologations.isPending && <Spinner label={t('common.states.loading')} />}
      {homologations.isSuccess && list.length === 0 && (
        <EmptyState title={t('academic.homologation.empty')} />
      )}

      {list.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.person.title')}</th>
                <th scope="col">{t('academic.subject.title')}</th>
                <th scope="col">{t('academic.homologation.sourceColumn')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.map((homologation) => (
                <tr key={homologation.id}>
                  <td>{homologation.personDisplayName}</td>
                  <td>{homologation.subjectName}</td>
                  <td>
                    {homologation.sourceType === 'EXTERNAL_INSTITUTION'
                      ? homologation.sourceInstitutionName
                      : schoolList.find((s) => s.id === homologation.sourceSchoolId)?.name}
                    {homologation.grade !== null && ` · ${homologation.grade}`}
                  </td>
                  <td>{t(`academic.homologation.status.${homologation.status}`)}</td>
                  <td>
                    {canReview && homologation.status === 'PENDING' && (
                      <>
                        <Button
                          variant="ghost"
                          loading={review.isPending}
                          onClick={() => review.mutate({ id: homologation.id, status: 'APPROVED' })}
                        >
                          {t('common.actions.approve')}
                        </Button>
                        <Button
                          variant="ghost"
                          loading={review.isPending}
                          onClick={() => review.mutate({ id: homologation.id, status: 'REJECTED' })}
                        >
                          {t('common.actions.reject')}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RequestHomologationForm({
  schools,
  onRequested,
}: {
  schools: readonly { id: string; name: string }[];
  onRequested: () => void;
}) {
  const { t } = useI18n();
  const [schoolId, setSchoolId] = useState('');
  const [person, setPerson] = useState<{ id: string; displayName: string } | null>(null);
  const [sourceType, setSourceType] = useState<HomologationSourceType>('EXTERNAL_INSTITUTION');
  const [sourceSchoolId, setSourceSchoolId] = useState('');
  const [sourceInstitutionName, setSourceInstitutionName] = useState('');
  const [grade, setGrade] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const subjects = useQuery({
    queryKey: ['academic', 'subjects', schoolId],
    queryFn: ({ signal }) => academicApi.listSubjects(schoolId, false, signal),
    enabled: schoolId.length > 0,
  });
  const [subjectId, setSubjectId] = useState('');

  const request = useMutation({
    mutationFn: async () => {
      let supportingFileId: string | null = null;
      if (file) {
        const session = await filesApi.requestUpload({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          declaredSize: file.size,
        });
        await filesApi.putBytes(session.uploadUrl, file);
        const completed = await filesApi.completeUpload(session.id, { fileName: file.name });
        supportingFileId = completed.id;
      }

      return academicApi.requestHomologation(schoolId, {
        personId: person!.id,
        subjectId,
        sourceType,
        sourceSchoolId: sourceType === 'INTERNAL_SCHOOL' ? sourceSchoolId || null : null,
        sourceInstitutionName:
          sourceType === 'EXTERNAL_INSTITUTION' ? sourceInstitutionName.trim() || null : null,
        supportingFileId,
        grade: grade ? Number(grade) : null,
      });
    },
    onSuccess: () => {
      setPerson(null);
      setSubjectId('');
      setSourceSchoolId('');
      setSourceInstitutionName('');
      setGrade('');
      setFile(null);
      setError(null);
      onRequested();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const subjectList = subjects.data ?? [];
  const canSubmit =
    person &&
    schoolId &&
    subjectId &&
    (sourceType === 'INTERNAL_SCHOOL' ? sourceSchoolId : sourceInstitutionName.trim());

  return (
    <form
      className="settings-card"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (canSubmit) request.mutate();
      }}
    >
      <h2 className="page-title">{t('academic.homologation.request')}</h2>
      {error && <Alert tone="danger">{error}</Alert>}

      {person ? (
        <span className="chip">
          {person.displayName}
          <button type="button" onClick={() => setPerson(null)}>
            ×
          </button>
        </span>
      ) : (
        <PersonPicker
          label={t('people.person.title')}
          placeholder={t('groups.group.choosePerson')}
          onPick={(id, displayName) => setPerson({ id, displayName })}
        />
      )}

      <div className="field">
        <label className="field-label" htmlFor="hom-school">
          {t('academic.school.title')}
        </label>
        <select
          id="hom-school"
          className="field-input"
          value={schoolId}
          onChange={(e) => {
            setSchoolId(e.target.value);
            setSubjectId('');
          }}
          required
        >
          <option value="">{t('common.actions.select')}</option>
          {schools.map((school) => (
            <option key={school.id} value={school.id}>
              {school.name}
            </option>
          ))}
        </select>
      </div>

      {schoolId && (
        <div className="field">
          <label className="field-label" htmlFor="hom-subject">
            {t('academic.subject.title')}
          </label>
          <select
            id="hom-subject"
            className="field-input"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            required
          >
            <option value="">{t('common.actions.select')}</option>
            {subjectList.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <fieldset className="form-grid">
        <legend>{t('academic.homologation.sourceLabel')}</legend>
        <label>
          <input
            type="radio"
            name="source-type"
            checked={sourceType === 'EXTERNAL_INSTITUTION'}
            onChange={() => setSourceType('EXTERNAL_INSTITUTION')}
          />
          {t('academic.homologation.source.EXTERNAL_INSTITUTION')}
        </label>
        <label>
          <input
            type="radio"
            name="source-type"
            checked={sourceType === 'INTERNAL_SCHOOL'}
            onChange={() => setSourceType('INTERNAL_SCHOOL')}
          />
          {t('academic.homologation.source.INTERNAL_SCHOOL')}
        </label>
      </fieldset>

      {sourceType === 'EXTERNAL_INSTITUTION' ? (
        <Field
          label={t('academic.homologation.institutionName')}
          name="hom-institution"
          value={sourceInstitutionName}
          onChange={(e) => setSourceInstitutionName(e.target.value)}
          required
        />
      ) : (
        <div className="field">
          <label className="field-label" htmlFor="hom-source-school">
            {t('academic.homologation.sourceSchool')}
          </label>
          <select
            id="hom-source-school"
            className="field-input"
            value={sourceSchoolId}
            onChange={(e) => setSourceSchoolId(e.target.value)}
            required
          >
            <option value="">{t('common.actions.select')}</option>
            {schools
              .filter((s) => s.id !== schoolId)
              .map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
          </select>
        </div>
      )}

      <Field
        label={t('academic.enrollment.grade')}
        name="hom-grade"
        type="number"
        value={grade}
        onChange={(e) => setGrade(e.target.value)}
        dir="ltr"
      />

      <div className="field">
        <label className="field-label" htmlFor="hom-file">
          {t('academic.homologation.certificate')}
        </label>
        <input
          id="hom-file"
          type="file"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      </div>

      <Button type="submit" variant="primary" loading={request.isPending} disabled={!canSubmit}>
        {t('academic.homologation.submit')}
      </Button>
    </form>
  );
}
